<script setup>
import { onBeforeMount, onBeforeUnmount, onMounted, ref, computed } from "vue";
// import { useMainStore } from "@/store/MainStore.js";
import { useAuthStore } from "@/store/AuthStore.js";
import { useFetch } from "@/plugins/fetch";
import { useToast } from "primevue/usetoast";

// PrimeVue and Argon Components
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import ArgonInput from "@/components/Argon/ArgonInput.vue";
import ArgonButton from "@/components/Argon/ArgonButton.vue";
import SettingsToggleCard from "@/components/Account/SettingsToggleCard.vue";
import LoginAttemptList from "@/components/Account/LoginAttemptList.vue";
import ChangePasswordCard from "@/components/Account/ChangePasswordCard.vue";
import SecuritySettings from "@/components/Account/SecuritySettings.vue";
import AccountDeletion from "@/components/Account/AccountDeletion.vue";

const body = document.getElementsByTagName("body")[0];
// const store = useMainStore();
const authStore = useAuthStore();
const fetch = useFetch();
const toast = useToast();

const user = ref(authStore.getUser);
const profilePicUrl = `https://avatar.oxro.io/avatar.svg?name=${user.value.full_name}&background=EBF4FF&color=0D6EFD`;
const address = ref({
  address_line: "",
  address_line_2: "",
  city: "",
  county: "",
  country: "",
  post_code: "",
});

const telegramEnrollmentLink = ref(null);
const loadingTelegram = ref(false);
const isConnected = ref(false);

const connectionStatus = computed(() => isConnected.value ? 'Connected' : 'Not Connected');
const connectionIcon = computed(() => isConnected.value ? 'fa-check-circle' : 'fa-times-circle');
const connectionColor = computed(() => isConnected.value ? 'text-success' : 'text-danger');

// --- METHODS ---
async function getAddress() {
  try {
    address.value = await fetch("users/me/address");
  } catch (error) {
    if (error.response && error.response.status !== 404) {
      toast.add({ severity: "error", summary: "Error", detail: "Failed to fetch address", life: 3000 });
    }
  }
}

const submitProfile = async () => {
  try {
    await fetch('users/me/address', { method: 'POST', body: address.value });
    await fetch('users/me/', { method: 'POST', body: { first_name: user.value.first_name, last_name: user.value.last_name } });
    toast.add({ severity: 'success', summary: 'Success', detail: 'Profile updated successfully!', life: 3000 });
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update profile', life: 3000 });
  }
}

const connectTelegram = async () => {
  loadingTelegram.value = true;
  try {
    const response = await fetch('user/notifications/telegram/enroll/', { method: 'POST' });
    isConnected.value = true;
    telegramEnrollmentLink.value = response.link;
    window.open(telegramEnrollmentLink.value, '_blank');
    toast.add({ severity: 'info', summary: 'Action Required', detail: 'Please complete the enrollment in Telegram', life: 5000 });
  } catch (error) {
    isConnected.value = false;
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to connect Telegram', life: 3000 });
  } finally {
    loadingTelegram.value = false;
  }
};

// --- LIFECYCLE HOOKS ---
onMounted(() => {
  getAddress();
  authStore.getUserInfo();
});

onBeforeMount(() => {
  body.classList.add("profile-page");
});

onBeforeUnmount(() => {
  body.classList.remove("profile-page");
});
</script>

<template>
  <div class="py-4 container-fluid">
    <Toast/>
    <div class="card shadow-lg">
      <div class="card-body p-3">
        <div class="row gx-4">
          <div class="col-auto">
            <div class="avatar avatar-xl position-relative">
              <img :src="profilePicUrl" alt="profile_image" class="shadow-sm w-100 border-radius-lg"/>
            </div>
          </div>
          <div class="col-auto my-auto">
            <div class="h-100">
              <h5 class="mb-1">{{ user.full_name }}</h5>
              <p class="mb-0 font-weight-bold text-sm text-capitalize">{{ user.plan }} Plan</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-4">
      <Tabs class="profile-tabs" value="edit-profile">
        <TabList>
          <Tab value="edit-profile">
            <i class="fas fa-user-edit me-2"></i>
            <span>Edit Profile</span>
          </Tab>
          <Tab value="security">
            <i class="fas fa-shield-alt me-2"></i>
            <span>Security</span>
          </Tab>
          <Tab value="settings">
            <i class="fas fa-sliders-h me-2"></i>
            <span>Settings & Integrations</span>
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="edit-profile">
          <div class="card">
            <div class="card-body">
              <form @submit.prevent="submitProfile">
                <p class="text-uppercase text-sm">User Information</p>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="input-firstname" class="form-control-label">First name</label>
                    <input class="form-control" type="text" v-model="user.first_name" id="input-firstname"/>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="input-lastname" class="form-control-label">Last name</label>
                    <ArgonInput type="text" v-model="user.last_name" id="input-lastname"/>
                  </div>
                  <div class="col-md-12 mb-3">
                    <label for="input-email" class="form-control-label">Email address</label>
                    <ArgonInput type="email" :model-value="user.email" id="input-email" disabled/>
                  </div>
                </div>
                <hr class="horizontal dark"/>
                <p class="text-uppercase text-sm">Address Information</p>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="input-addr" class="form-control-label">Address</label>
                    <ArgonInput type="text" id="input-addr" v-model="address.address_line"/>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="input-addr2" class="form-control-label">Address 2</label>
                    <ArgonInput type="text" id="input-addr2" v-model="address.address_line_2"/>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label for="input-city" class="form-control-label">City</label>
                    <ArgonInput type="text" id="input-city" v-model="address.city"/>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label for="input-county" class="form-control-label">County</label>
                    <ArgonInput type="text" id="input-county" v-model="address.county"/>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label for="input-country" class="form-control-label">Country</label>
                    <ArgonInput type="text" id="input-country" v-model="address.country"/>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label for="input-postcode" class="form-control-label">Post code</label>
                    <ArgonInput type="text" id="input-postcode" v-model="address.post_code"/>
                  </div>
                </div>
                <div class="text-end">
                  <ArgonButton color="success" type="submit">Save Changes</ArgonButton>
                </div>
              </form>
            </div>
          </div>
          </TabPanel>
          <TabPanel value="security">
          <div class="row">
            <div class="col-12 mb-4">
              <SecuritySettings />
            </div>
            <div class="col-md-6 mb-4">
              <ChangePasswordCard/>
            </div>
            <div class="col-md-6 mb-4">
              <LoginAttemptList/>
            </div>
            <div class="col-12 mb-4">
              <AccountDeletion />
            </div>
          </div>
          </TabPanel>
          <TabPanel value="settings">
          <div class="row">
            <div class="col-md-6 mb-4">
              <div class="card h-100">
                <div class="card-header pb-0">
                  <h6 class="mb-0">Integrations</h6>
                </div>
                <div class="card-body">
                  <p class="text-uppercase text-sm">Connections</p>
                  <div class="d-flex align-items-center">
                    <ArgonButton type="button" class="telegram-button btn btn-twitter mb-0" @click="connectTelegram"
                                 :loading="loadingTelegram" :disabled="loadingTelegram">
                      <i class="fab fa-telegram-plane"></i> Connect Telegram
                    </ArgonButton>
                    <span class="ms-3 d-flex align-items-center">
                        <i :class="`fa ${connectionIcon} ${connectionColor} me-2`"></i>
                        {{ connectionStatus }}
                      </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6 mb-4">
              <SettingsToggleCard/>
            </div>
          </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
  </div>
</template>
<style>
.profile-tabs .p-tablist {
  background: var(--bs-white);
  border: 1px solid var(--bs-gray-200);
  border-radius: 0.75rem;
  margin-bottom: 0.25rem;
  padding: 0.25rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
}

.profile-tabs .p-tab {
  background: transparent;
  border: none;
  margin-right: 0.5rem;
  border-radius: 0.5rem;
  color: var(--bs-gray-600);
  font-weight: 500;
  padding: 0.75rem 1.25rem;
  transition: all 0.2s ease-in-out;
}

.profile-tabs .p-tab:not(.p-disabled):focus {
  box-shadow: none;
}

.profile-tabs .p-tab:not(.p-disabled):hover {
  background: var(--bs-gray-100);
  color: var(--bs-dark);
}

.profile-tabs .p-tab.p-highlight,
.profile-tabs .p-tab.p-tab-active {
  background-color: var(--calid-hot);
  color: var(--bs-white);
}

.dark-theme .profile-tabs .p-tablist,
.dark-version .profile-tabs .p-tablist {
  background: var(--bs-gray-800);
  border-color: var(--bs-gray-700);
  box-shadow: none;
}

.dark-theme .profile-tabs .p-tab,
.dark-version .profile-tabs .p-tab {
  color: var(--bs-gray-300);
}

.dark-theme .profile-tabs .p-tab:not(.p-disabled):hover,
.dark-version .profile-tabs .p-tab:not(.p-disabled):hover {
  background: var(--bs-gray-700);
  color: var(--bs-white);
}

.dark-theme .profile-tabs .p-tab.p-highlight,
.dark-theme .profile-tabs .p-tab.p-tab-active,
.dark-version .profile-tabs .p-tab.p-highlight,
.dark-version .profile-tabs .p-tab.p-tab-active {
  background-color: var(--calid-hot);
  color: var(--bs-white);
}
.dark-theme .profile-tabs .p-tab.p-highlight,
.dark-theme .profile-tabs .p-tab.p-tab-active:hover,
.dark-version .profile-tabs .p-tab.p-highlight,
.dark-version .profile-tabs .p-tab.p-tab-active:hover {
  background-color: var(--calid-hot-hover-blacker);
  color: var(--bs-white);
}
.p-tabpanels {
  border-radius: 0.75rem;
}
</style>
